<script setup>
import { computed, ref } from 'vue';
import TechnicalPropertyInfo from 'components/competitor-app-v2/technical-properties/TechnicalPropertyInfo.vue';
import CieLabChart from 'components/competitor-app-v2/technical-properties/CieLabChart.vue';
import VueSlider from 'vue-3-slider-component';
import { angleSortFunction } from 'utils/competitorApp';

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  color: {
    type: String,
    required: false,
    default: '#7a7a7a',
  },
});

const byApplicationType = computed(() => {
  const result = {};

  Object.values(props.data).forEach((properties) => {
    properties.forEach((property) => {
      if (!result[property.applicationType]) {
        result[property.applicationType] = {};
      }

      result[property.applicationType][property.propertyType] = property.value;
    });
  });

  return result;
});

const angles = computed(() => {
  if (byApplicationType.value) {
    const aAngles = new Set();
    const bAngles = new Set();

    Object.values(byApplicationType.value).forEach((row) => {
      row.a.forEach((valueRow) => {
        if (valueRow.value !== null) {
          aAngles.add(valueRow.code);
        }
      });

      row.b.forEach((valueRow) => {
        if (valueRow.value !== null) {
          bAngles.add(valueRow.code);
        }
      });
    });

    return [...aAngles]
      .sort(angleSortFunction)
      .filter((angle) => bAngles.has(angle));
  }

  return [];
});

const angleOptions = computed(() => (
  angles.value.map((value) => ({
    value,
    label: `45°:as${value}°`
  }))
));

const angleMarks = computed(() => (
  Object.fromEntries(
    angleOptions.value.map((row) => [row.value, `${row.value}°`])
  )
));

const activeAngle = ref(angles.value[0]);
</script>

<template>
  <div class="ce-technicalPropertyCard ce-technicalPropertyCard--cieLab">
    <div class="flex items-center mb3">
      <TechnicalPropertyInfo
        property-type="a" />
      &nbsp;/&nbsp;
      <TechnicalPropertyInfo
        property-type="b" />
    </div>

    <div
      class="ce-cieLabCard-angleSelect"
      :style="{ '--slider-highlight-color': color }">
      <div class="slider-wrapper">
        <VueSlider
          :data="angleOptions"
          v-model="activeAngle"
          :marks="angleMarks"
          contained />
      </div>

      <div class="image-wrapper">
        <img :src="`resources/themes/v2/img/angle/${activeAngle}.png`">
      </div>
    </div>

    <div class="innerCardsWrapper">
      <CieLabChart
        v-for="(values, applicationType) in byApplicationType"
        :key="`cielab_${applicationType}`"
        :values="values"
        :application-type="applicationType"
        :color="color"
        :angle="activeAngle" />
    </div>
  </div>
</template>

<style lang="less">
@import (reference) '~styles/includes/base';
@sliderMainColor: @ce-slider-filter-main-color;

.ce-technicalPropertiesByMedium {
  .ce-technicalPropertiesWrapper {
    .ce-technicalPropertyCard--cieLab {
      .ce-cieLabCard-angleSelect {
        margin-bottom: @space-5;
        display: flex;
        flex-direction: column-reverse;
        gap: @space-3;

        .image-wrapper {
          width: 200px;
          align-self: center;

          img {
            width: 100%;
            display: block;
          }
        }

        .slider-wrapper {
          flex: 1 1 auto;
          padding: 28px 28px 16px;

          .vue-slider {
            .vue-slider-rail {
              background-color: @sliderMainColor;
              border-radius: 15px;
            }

            .vue-slider-process {
              background-color: @sliderMainColor;
              border-radius: 15px;
            }

            .vue-slider-dot-handle {
              cursor: pointer;
              width: 100%;
              height: 100%;
              border-radius: 50%;
              background-color: var(--slider-highlight-color);
              box-sizing: border-box;
              box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
            }

            .vue-slider-dot-tooltip-inner {
              color: #fff;
              border-color: @sliderMainColor;
              background-color: @sliderMainColor;
              font-weight: 600;
            }
          }
        }

        @media screen and (min-width: @breakpoint-xxs) {
          align-items: flex-end;
          flex-direction: row;
          gap: @space-4;

          .image-wrapper {
            width: 120px;
          }
        }

        @media screen and (min-width: @breakpoint-sm) {
          gap: @space-5;

          .image-wrapper {
            width: 200px;
          }
        }
      }

      .innerCardsWrapper {
        .ce-technicalPropertyCard {
          background: @color-mono-88;
        }
      }

      .ce-cielab-chart {
        .applicationType {
          margin-bottom: @space-2;
          .ms(-1);
          font-weight: 600;
        }

        canvas {
          width: 100%;
          height: 300px;
        }
      }

      @media screen and (min-width: @breakpoint-xl) {
        grid-column: span 2;

        .innerCardsWrapper {
          display: flex;
          gap: @space-2;
        }
      }

      @media screen and (min-width: @breakpoint-xxl) {
        grid-column: span 3;
      }
    }
  }
}
</style>
